import { categoriesFor, type SpaceKind } from '@core/categories'
import type { Space } from '@core/session'

/**
 * Where does this belong, and what kind of cost is it?
 *
 * Shown on the verification screen and nowhere else. The sender is never asked
 * (spec §189: "no category, no space, no 'is this Home or Restaurant?'") — the
 * pipeline guesses from supplier history, and this is where a wrong guess gets
 * corrected once. The correction is written back to the supplier, so the next
 * invoice from them arrives already right.
 *
 * Presented as choices rather than dropdowns because there are two spaces and
 * eight categories. A select would hide the options behind a tap and make the
 * common case — glance, see it is already correct, move on — slower.
 */
export function RoutingPicker({
  spaces,
  spaceId,
  category,
  onSpace,
  onCategory,
}: {
  spaces: Space[]
  spaceId: string
  category: string | null
  onSpace: (id: string) => void
  onCategory: (c: string | null) => void
}) {
  const current = spaces.find((s) => s.id === spaceId)
  const kind = (current?.kind ?? 'household') as SpaceKind

  return (
    <div className="mb-4">
      {spaces.length > 1 && (
        <>
          <p className="text-sm text-muted mb-1.5">Waarvoor is dit?</p>
          <div className="flex gap-2 mb-3">
            {spaces.map((s) => (
              <button
                key={s.id}
                onClick={() => onSpace(s.id)}
                className={`flex-1 min-h-[3rem] rounded-xl border text-sm font-medium transition-colors ${
                  s.id === spaceId
                    ? s.kind === 'business'
                      ? 'border-restaurant bg-restaurant/10 text-restaurant'
                      : 'border-household bg-household/10 text-household'
                    : 'border-line text-muted'
                }`}
              >
                {s.name}
              </button>
            ))}
          </div>
        </>
      )}

      <p className="text-sm text-muted mb-1.5">Soort kost</p>
      <div className="flex flex-wrap gap-1.5">
        {categoriesFor(kind).map((c) => (
          <button
            key={c}
            onClick={() => onCategory(c === category ? null : c)}
            className={`px-3 py-2 rounded-lg text-sm border transition-colors ${
              c === category ? 'border-ink bg-ink/5 font-medium' : 'border-line text-muted'
            }`}
          >
            {c}
          </button>
        ))}
      </div>
    </div>
  )
}
